import { memo, useCallback } from 'react';

import { ChevronRightIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { type WorkspaceAgentActionsMenuGroupId } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentActionsMenuTasks';
import { type WorkspaceAgentIconComponent } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/types';
import { preserveWorkspaceAgentComposerFocus } from '~/components/v2/components/workspace/workspace-agent-panel/utils/preserveWorkspaceAgentComposerFocus';

export type WorkspaceAgentActionsMenuGroupRowProps = {
  description: string;
  groupId: WorkspaceAgentActionsMenuGroupId;
  icon: WorkspaceAgentIconComponent;
  iconColorClassName: string;
  label: string;
  onSelect: (groupId: WorkspaceAgentActionsMenuGroupId) => void;
};

export const WorkspaceAgentActionsMenuGroupRow = memo(
  ({
    description,
    groupId,
    icon: Icon,
    iconColorClassName,
    label,
    onSelect,
  }: WorkspaceAgentActionsMenuGroupRowProps) => {
    const handleClick = useCallback(() => {
      onSelect(groupId);
    }, [groupId, onSelect]);

    return (
      <button
        className="flex w-full min-w-0 items-start gap-2 rounded-6 px-2 py-1.5 text-left ads:hover:bg-hover-subtle hover:bg-gray-4"
        data-group-id={groupId}
        data-testid="WORKSPACE_AGENT_ACTIONS_MENU_GROUP"
        onClick={handleClick}
        onMouseDown={preserveWorkspaceAgentComposerFocus}
        type="button"
      >
        <span aria-hidden className={cn('mt-0.5 shrink-0', iconColorClassName)}>
          <Icon size="sm" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-medium ads:text-primary text-12 text-gray-12 leading-4">{label}</span>
          <span className="block whitespace-normal ads:text-metadata text-12 text-gray-10 leading-4">
            {description}
          </span>
        </span>
        <ChevronRightIcon aria-hidden className="mt-0.5 shrink-0 ads:text-icon-subtle text-gray-9" size="xs" />
      </button>
    );
  },
);

WorkspaceAgentActionsMenuGroupRow.displayName = 'WorkspaceAgentActionsMenuGroupRow';
